class HomepageModuleCarousel extends HTMLElement {
  connectedCallback() {
    this.mainSwiper = null;
    this.initializeTimer = null;
    this.initializeAttempts = 0;
    this.thumbnailButtons = Array.from(this.querySelectorAll('[data-slide-to]'));
    this.videos = Array.from(this.querySelectorAll('.homepage-module-carousel__video'));
    this.images = Array.from(this.querySelectorAll('img'));
    this.progressFrame = null;
    this.handleThumbnailClick = this.handleThumbnailClick.bind(this);
    this.handleVideoMetadata = this.handleVideoMetadata.bind(this);
    this.handleImageLoad = this.handleImageLoad.bind(this);

    this.thumbnailButtons.forEach((button) => {
      button.addEventListener('click', this.handleThumbnailClick);
    });
    this.videos.forEach((video) => {
      video.addEventListener('loadedmetadata', this.handleVideoMetadata);
    });
    this.images.forEach((image) => image.addEventListener('load', this.handleImageLoad));

    this.initializeWhenReady();
  }

  handleThumbnailClick(event) {
    this.goToSlide(event.currentTarget.dataset.slideTo);
  }

  initializeWhenReady() {
    if (window.Swiper) {
      this.initialize();
      return;
    }

    if (this.initializeAttempts >= 100) return;
    this.initializeAttempts += 1;

    this.initializeTimer = window.setTimeout(() => this.initializeWhenReady(), 50);
  }

  initialize() {
    if (this.mainSwiper) return;

    const mainElement = this.querySelector('[data-carousel-main]');
    if (!mainElement) return;

    const slideCount = mainElement.querySelectorAll('.swiper-slide').length;
    const shouldAutoplay = slideCount > 1
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.mainSwiper = new Swiper(mainElement, {
      a11y: {
        enabled: true,
        prevSlideMessage: this.dataset.previousLabel,
        nextSlideMessage: this.dataset.nextLabel,
        slideLabelMessage: '{{index}} / {{slidesLength}}',
      },
      autoHeight: true,
      allowTouchMove: slideCount > 1,
      effect: 'slide',
      speed: 600,
      loop: slideCount > 1,
      autoplay: shouldAutoplay
        ? { delay: this.getAutoplayDelay(), disableOnInteraction: false }
        : false,
      on: {
        slideChange: () => {
          this.updateThumbnailState();
          this.updateContent();
          this.syncActiveMedia();
        },
      },
    });

    this.updateThumbnailState();
    this.updateContent();
    this.syncActiveMedia();
  }

  getAutoplayDelay() {
    return Number(this.dataset.autoplayDelay) || 5000;
  }

  getActiveSlide() {
    if (!this.mainSwiper) return null;
    return this.mainSwiper.slides[this.mainSwiper.activeIndex] || null;
  }

  getActiveVideo() {
    const activeSlide = this.getActiveSlide();
    if (!activeSlide) return null;

    const videoVariant = window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop';
    return activeSlide.querySelector(`.homepage-module-carousel__video--${videoVariant}`) || null;
  }

  updateContent() {
    const activeSlide = this.getActiveSlide();
    const content = this.querySelector('[data-carousel-content]');
    if (!activeSlide || !content) return;

    const contentCopy = content.querySelector('.homepage-module-carousel__content-copy');
    const headingDesktop = content.querySelector('[data-carousel-heading-desktop]');
    const headingMobile = content.querySelector('[data-carousel-heading-mobile]');
    const descriptionDesktop = content.querySelector('[data-carousel-description-desktop]');
    const descriptionMobile = content.querySelector('[data-carousel-description-mobile]');
    const slideData = activeSlide.dataset;

    contentCopy.style.setProperty('--homepage-module-carousel-slide-heading-font-size-desktop', `${slideData.headingFontSizeDesktop}px`);
    contentCopy.style.setProperty('--homepage-module-carousel-slide-heading-font-size-mobile', `${slideData.headingFontSizeMobile}px`);
    contentCopy.style.setProperty('--homepage-module-carousel-slide-heading-color-desktop', slideData.headingColorDesktop);
    contentCopy.style.setProperty('--homepage-module-carousel-slide-heading-color-mobile', slideData.headingColorMobile);
    contentCopy.style.setProperty('--homepage-module-carousel-slide-description-font-size-desktop', `${slideData.descriptionFontSizeDesktop}px`);
    contentCopy.style.setProperty('--homepage-module-carousel-slide-description-font-size-mobile', `${slideData.descriptionFontSizeMobile}px`);
    contentCopy.style.setProperty('--homepage-module-carousel-slide-description-color-desktop', slideData.descriptionColorDesktop);
    contentCopy.style.setProperty('--homepage-module-carousel-slide-description-color-mobile', slideData.descriptionColorMobile);
    contentCopy.style.setProperty('--homepage-module-carousel-slide-description-opacity-desktop', slideData.descriptionOpacityDesktop);
    contentCopy.style.setProperty('--homepage-module-carousel-slide-description-opacity-mobile', slideData.descriptionOpacityMobile);

    headingDesktop.textContent = slideData.headingDesktop || '';
    headingMobile.textContent = slideData.headingMobile || '';
    descriptionDesktop.textContent = slideData.descriptionDesktop || '';
    descriptionMobile.textContent = slideData.descriptionMobile || '';
  }

  handleVideoMetadata() {
    this.syncActiveMedia();
  }

  handleImageLoad() {
    if (this.mainSwiper) this.mainSwiper.updateAutoHeight(0);
  }

  syncActiveMedia() {
    if (!this.mainSwiper) return;

    this.videos.forEach((video) => {
      if (video !== this.getActiveVideo()) video.pause();
    });

    const activeVideo = this.getActiveVideo();
    const duration = activeVideo && Number.isFinite(activeVideo.duration) && activeVideo.duration > 0
      ? activeVideo.duration * 1000
      : this.getAutoplayDelay();

    if (activeVideo) {
      activeVideo.currentTime = 0;
      activeVideo.play().catch(() => {});
    }

    if (this.mainSwiper.params.autoplay && this.mainSwiper.autoplay) {
      this.mainSwiper.params.autoplay.delay = duration;
      this.mainSwiper.autoplay.stop();
      this.mainSwiper.autoplay.start();
    }

    this.startProgress(duration);
  }

  startProgress(duration = this.getAutoplayDelay()) {
    this.stopProgress();
    this.thumbnailButtons.forEach((button) => {
      button.style.setProperty('--thumbnail-progress', '0%');
    });

    if (!this.mainSwiper || this.thumbnailButtons.length < 2) return;

    const activeButton = this.thumbnailButtons[this.mainSwiper.realIndex];
    if (!activeButton) return;

    const startedAt = performance.now();
    const updateProgress = (timestamp) => {
      const progress = Math.min((timestamp - startedAt) / duration, 1);
      activeButton.style.setProperty('--thumbnail-progress', `${progress * 100}%`);
      if (progress < 1 && this.mainSwiper) {
        this.progressFrame = window.requestAnimationFrame(updateProgress);
      }
    };

    this.progressFrame = window.requestAnimationFrame(updateProgress);
  }

  stopProgress() {
    if (this.progressFrame) {
      window.cancelAnimationFrame(this.progressFrame);
      this.progressFrame = null;
    }
  }

  destroy() {
    if (this.initializeTimer) {
      window.clearTimeout(this.initializeTimer);
      this.initializeTimer = null;
    }

    this.stopProgress();
    this.thumbnailButtons.forEach((button) => {
      button.removeEventListener('click', this.handleThumbnailClick);
      button.style.removeProperty('--thumbnail-progress');
    });
    this.videos.forEach((video) => {
      video.removeEventListener('loadedmetadata', this.handleVideoMetadata);
      video.pause();
    });
    this.images.forEach((image) => image.removeEventListener('load', this.handleImageLoad));

    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
      this.mainSwiper = null;
    }
  }

  goToSlide(index) {
    if (!this.mainSwiper) return;
    this.mainSwiper.slideToLoop(Number(index));
  }

  updateThumbnailState() {
    if (!this.mainSwiper) return;
    const activeIndex = this.mainSwiper.realIndex;

    this.thumbnailButtons.forEach((button, index) => {
      const isActive = index === activeIndex;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', String(isActive));
    });
  }
  disconnectedCallback() {
    this.destroy();
  }
}

if (!customElements.get('homepage-module-carousel')) {
  customElements.define('homepage-module-carousel', HomepageModuleCarousel);
}

document.addEventListener('shopify:block:select', (event) => {
  const slide = event.target && event.target.closest('[data-slide-index]');
  if (!slide) return;

  const carousel = slide.closest('homepage-module-carousel');
  if (carousel) carousel.goToSlide(slide.dataset.slideIndex);
});
